'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as z from 'zod'

import { Button } from '../components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form'
import { Input } from '../components/ui/input'
import {
  confirmPasswordResetService,
  validatePasswordResetKeyService,
} from '../services/authService'

const resetPasswordSchema = z
  .object({
    password1: z.string().min(8, 'Password must be at least 8 characters long'),
    password2: z.string(),
  })
  .refine((data) => data.password1 === data.password2, {
    message: 'Passwords do not match',
    path: ['password2'],
  })

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

export default function ResetPasswordForm() {
  const [searchParams] = useSearchParams()
  const key = searchParams.get('key')
  const navigate = useNavigate()
  const [error, setError] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password1: '',
      password2: '',
    },
  })

  useEffect(() => {
    // Validate that we have a key
    if (!key || key.trim() === '') {
      setError('Invalid reset link. Please request a new password reset.')
    }
  }, [key])

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!key || key.trim() === '') {
      setError('Invalid reset link. Please request a new password reset.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await confirmPasswordResetService(key, data.password1)

      if (result.error) {
        console.error('Password reset confirmation failed:', result.error)
        // Check if it's an axios error with response data
        if (result.error.response?.data?.errors?.[0]?.message) {
          setError(result.error.response.data.errors[0].message)
        } else {
          setError(
            'Failed to reset password. The link may be invalid or expired.',
          )
        }
      } else if (result.success && result.requiresLogin) {
        // 401 response - password reset successful but requires manual login
        console.log('Password reset successful, redirecting to login')
        navigate('/login')
      } else {
        // 200 response - password reset successful and user is auto-logged in
        console.log('Password reset successful, user auto-logged in')
        navigate('/login')
      }
    } catch (err) {
      console.error('Password reset confirmation error:', err)
      setError('Network error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!key || key.trim() === '') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <img src="/dd_logo.png" alt="DayDaCo" className="h-12" />
              </div>
              <CardTitle className="text-2xl font-semibold text-gray-900">
                Invalid Reset Link
              </CardTitle>
              <CardDescription className="text-gray-600">
                This password reset link is invalid.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center space-y-4">
                <Link
                  to="/forgot-password"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Request a new password reset
                </Link>
                <div>
                  <Link
                    to="/login"
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Back to Login
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <Card className="border border-gray-200 shadow-sm">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src="/dd_logo.png" alt="DayDaCo" className="h-12" />
            </div>
            <CardTitle className="text-2xl font-semibold text-gray-900">
              Set New Password
            </CardTitle>
            <CardDescription className="text-gray-600">
              Enter your new password below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
              >
                <FormField
                  control={form.control}
                  name="password1"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium">
                        New Password
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Enter your new password"
                          className="h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={loading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password2"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-gray-700 font-medium">
                        Confirm New Password
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder="Confirm your new password"
                          className="h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          disabled={loading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {error && (
                  <div className="text-sm text-red-600 bg-red-50 p-3 rounded-md border border-red-200">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg disabled:bg-[#E5E7EB] disabled:text-[#111928]"
                  disabled={
                    loading ||
                    !form.watch('password1') ||
                    !form.watch('password2')
                  }
                >
                  {loading ? (
                    <i className="fa-duotone fa-solid fa-spinner-third fa-spin" />
                  ) : (
                    'Update Password'
                  )}
                </Button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    Back to Login
                  </Link>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
